import { CompanyIntegrationStatus } from "@/components/integrations/company/company-integrations-sync";
import { DropdownMenuContent } from "@/components/ui/dropdown-menu";
import {
  CompanyCrmIntegration,
  CrmIntegrationProviderSupported,
} from "@shaarp/common";
import { CreateNewCompany } from "./create-new-company";
import { SearchResults } from "./search-results";
import { SyncedIntegrations } from "./synced-integrations";

type IntegrationDropdownContentProps = {
  integrations: CompanyIntegrationStatus[];
  onSync: (
    integrationKey: CrmIntegrationProviderSupported,
    externalCompanyId?: string,
  ) => void;
  onOpenExternal: (crmCompany: CompanyCrmIntegration) => void;
};

/**
 * One section per connected CRM rather than one global "synced / not synced"
 * split: with two CRMs, a company can legitimately be in one and not the other,
 * and collapsing that into a single flag would hide the half that still needs
 * sending.
 */
export const IntegrationSyncSections = ({
  integrations,
  onSync,
  onOpenExternal,
}: IntegrationDropdownContentProps) => {
  const syncedIntegrations = integrations.filter(
    (integration) => integration.isSynced,
  );
  const unsyncedIntegrations = integrations.filter(
    (integration) => !integration.isSynced,
  );

  return (
    <>
      {syncedIntegrations.length > 0 && (
        <SyncedIntegrations
          integrations={syncedIntegrations}
          onSync={onSync}
          onOpenExternal={onOpenExternal}
        />
      )}

      {unsyncedIntegrations
        .filter((integration) => integration.possibleMatches.length === 0)
        .map((integration) => (
          <CreateNewCompany
            key={integration.key}
            integration={integration}
            onSync={onSync}
          />
        ))}

      {unsyncedIntegrations
        .filter((integration) => integration.possibleMatches.length > 0)
        .map((integration) => (
          <SearchResults
            key={integration.key}
            integration={integration}
            crmCompanies={integration.possibleMatches}
            onSyncToCompany={(externalCompanyId) =>
              onSync(integration.key, externalCompanyId)
            }
            onOpenExternal={onOpenExternal}
          />
        ))}
    </>
  );
};

export const IntegrationDropdownContent = (
  props: IntegrationDropdownContentProps,
) => {
  return (
    <DropdownMenuContent align="end" side="top" className="p-2 w-96">
      <IntegrationSyncSections {...props} />
    </DropdownMenuContent>
  );
};
